import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatCNPJ } from '@/lib/utils/formatters';
import { useServicosNfseHeader } from './hooks';

interface ServicosNfseHeaderProps {
  onAplicar: () => void;
}

type HeaderModel = ReturnType<typeof useServicosNfseHeader>;
type EmpresaAgrupadaItem = HeaderModel['dropdownEmpresasAgrupadasFiltradas'][number];
type ContribuinteItem = HeaderModel['contribuintesFiltrados'][number];

function HeaderBrand() {
  return (
    <Link to="/" className="flex min-w-0 items-center gap-4">
      <img
        src="/icone_novo.png"
        alt="Sistema IBS/CBS"
        className="h-14 w-14 rounded-xl object-cover"
      />
      <div className="h-8 w-px bg-[#a8cf45]" />
      <div className="min-w-0">
        <h1 className="font-semibold leading-none tracking-[-0.02em] text-[#1f2937]">
          Sistema IBS/CBS
        </h1>
        <p className="mt-1 text-xs text-[#6b7280]">Lei 214/2025</p>
      </div>
    </Link>
  );
}

function SelectCheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="#404040"
      className="h-2.5 w-2.5"
    >
      <path
        fillRule="evenodd"
        d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SelectIndicator({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
        selected ? 'border-[#404040]' : 'border-muted-foreground/50'
      )}
    >
      {selected && <SelectCheckIcon />}
    </span>
  );
}

function getSelectedEmpresaLabel(u: HeaderModel) {
  if (!u.cnpjSelecionado || !u.contribuintes) return null;

  const selected = u.contribuintes.find(
    contribuinte => contribuinte.CNPJ_Contribuinte === u.cnpjSelecionado
  );

  if (!selected) return null;

  return `${selected.Nome} - ${formatCNPJ(selected.CNPJ_Contribuinte)}`;
}

function EmpresaSelectValueContent({ u }: { u: HeaderModel }) {
  const selectedLabel = getSelectedEmpresaLabel(u);
  const placeholder = u.modoGruposEmpresa ? 'Selecione o grupo' : 'Selecione a empresa';

  if (selectedLabel) return selectedLabel;

  return <span className="text-foreground">{placeholder}</span>;
}

function EmpresaSingleOption({
  contribuinte,
  selectedValue,
}: {
  contribuinte: ContribuinteItem;
  selectedValue: string;
}) {
  const isSelected = contribuinte.CNPJ_Contribuinte === selectedValue;

  return (
    <SelectItem
      value={contribuinte.CNPJ_Contribuinte}
      className={cn(
        isSelected ? 'bg-accent data-[state=checked]:bg-accent' : '',
        '[&>span:first-child]:hidden hover:bg-accent/50 focus:bg-accent/50 focus:text-foreground'
      )}
    >
      <div className="flex w-full items-center gap-2">
        <SelectIndicator selected={isSelected} />
        <span className="truncate">
          {contribuinte.Nome} - {formatCNPJ(contribuinte.CNPJ_Contribuinte)}
        </span>
      </div>
    </SelectItem>
  );
}

function EmpresaGroupedSingleOption({
  empresa,
  selectedValue,
}: {
  empresa: Extract<EmpresaAgrupadaItem, { kind: 'single' }>['empresa'];
  selectedValue: string;
}) {
  const isSelected = empresa.CNPJ_Contribuinte === selectedValue;

  return (
    <SelectItem
      value={empresa.CNPJ_Contribuinte}
      className={cn(
        isSelected ? 'bg-accent data-[state=checked]:bg-accent' : '',
        '[&>span:first-child]:hidden hover:bg-accent/50 focus:bg-accent/50 focus:text-foreground'
      )}
    >
      <div className="flex w-full items-center gap-2">
        <SelectIndicator selected={isSelected} />
        <span className="truncate">
          {empresa.Nome} - {formatCNPJ(empresa.CNPJ_Contribuinte)}
        </span>
      </div>
    </SelectItem>
  );
}

function EmpresaGroupOption({
  item,
  u,
}: {
  item: Extract<EmpresaAgrupadaItem, { kind: 'group' }>;
  u: HeaderModel;
}) {
  const open = !!u.gruposEmpresaAbertos[item.groupKey];
  const isSelected = item.matriz.CNPJ_Contribuinte === u.cnpjSelecionado;

  const handleSelectGroup = () => {
    const cnpjsCsv = [
      item.matriz.CNPJ_Contribuinte,
      ...item.vinculadas.map(vinculada => vinculada.CNPJ_Contribuinte),
    ].join(',');

    u.handleGrupoChange(item.matriz.CNPJ_Contribuinte, cnpjsCsv);
    u.setEmpresaSelectOpen(false);
  };

  return (
    <div key={item.groupKey}>
      <div
        className={cn(
          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none',
          isSelected ? 'bg-accent' : 'hover:bg-accent/50'
        )}
      >
        <div
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
          onPointerDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            handleSelectGroup();
          }}
        >
          <SelectIndicator selected={isSelected} />
          <span className="truncate">
            {item.matriz.Nome} - {formatCNPJ(item.matriz.CNPJ_Contribuinte)}
          </span>
        </div>

        <button
          type="button"
          className="inline-flex shrink-0 items-center justify-center rounded-sm p-1 hover:bg-accent/50"
          aria-label={open ? 'Recolher grupo' : 'Expandir grupo'}
          onPointerDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            u.setGruposEmpresaAbertos(prev => ({
              ...prev,
              [item.groupKey]: !prev[item.groupKey],
            }));
          }}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open ? 'rotate-180' : ''
            )}
          />
        </button>
      </div>

      {open && (
        <div className="flex flex-col">
          {item.vinculadas.map(vinculada => (
            <div
              key={vinculada.CNPJ_Contribuinte}
              className="flex w-full items-center gap-2 py-1.5 pl-8 pr-2 text-sm text-muted-foreground"
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/30" />
              <span className="truncate">
                {vinculada.Nome} - {formatCNPJ(vinculada.CNPJ_Contribuinte)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmpresaSearchInput({ u }: { u: HeaderModel }) {
  return (
    <div className="border-b p-2">
      <Input
        type="text"
        placeholder="Buscar por nome ou CNPJ..."
        value={u.searchEmpresa}
        onChange={event => u.setSearchEmpresa(event.target.value)}
        className="h-8"
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
      />
    </div>
  );
}

function EmpresaOptionsContent({ u }: { u: HeaderModel }) {
  if (u.isLoadingContribuintes) {
    return (
      <SelectItem value="loading" disabled>
        Carregando...
      </SelectItem>
    );
  }

  if (u.modoGruposEmpresa && u.dropdownEmpresasAgrupadasFiltradas.length > 0) {
    return u.dropdownEmpresasAgrupadasFiltradas.map(item =>
      item.kind === 'single' ? (
        <EmpresaGroupedSingleOption
          key={item.empresa.CNPJ_Contribuinte}
          empresa={item.empresa}
          selectedValue={u.cnpjSelecionado}
        />
      ) : (
        <EmpresaGroupOption key={item.groupKey} item={item} u={u} />
      )
    );
  }

  if (!u.modoGruposEmpresa && u.contribuintesFiltrados.length > 0) {
    return u.contribuintesFiltrados.map(contribuinte => (
      <EmpresaSingleOption
        key={contribuinte.CNPJ_Contribuinte}
        contribuinte={contribuinte}
        selectedValue={u.cnpjSelecionado}
      />
    ));
  }

  if (u.searchEmpresa) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        Nenhuma empresa encontrada para "{u.searchEmpresa}"
      </div>
    );
  }

  return (
    <SelectItem value="empty" disabled>
      Nenhuma empresa disponível
    </SelectItem>
  );
}

function EmpresaGroupModeButton({ u }: { u: HeaderModel }) {
  return (
    <button
      type="button"
      className={cn(
        'relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors',
        u.modoGruposEmpresa
          ? 'border-[#aed561] bg-background text-[#aed561] hover:bg-[#aed561]/15 hover:text-[#8ab83a]'
          : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
      title={u.modoGruposEmpresa ? 'Mostrar lista simples' : 'Mostrar por grupos (matriz/filiais)'}
      aria-label={
        u.modoGruposEmpresa ? 'Desativar agrupamento de empresas' : 'Ativar agrupamento de empresas'
      }
      onClick={u.handleToggleModoGruposEmpresa}
    >
      <UsersThreeIcon size={22} weight="bold" />
      {u.modoGruposEmpresa && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-[#aed561] bg-[#f4fae2]">
          <SelectCheckIcon />
        </span>
      )}
    </button>
  );
}

function EmpresaField({ u }: { u: HeaderModel }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">Empresa</label>
      <div className="flex items-center gap-1.5">
        <Select
          open={u.empresaSelectOpen}
          onOpenChange={u.setEmpresaSelectOpen}
          value={u.cnpjSelecionado}
          onValueChange={u.handleEmpresaChange}
          disabled={u.isLoadingContribuintes}
        >
          <SelectTrigger className="h-9 w-[280px] bg-background">
            <SelectValue
              placeholder={u.modoGruposEmpresa ? 'Selecione o grupo' : 'Selecione a empresa'}
            >
              <EmpresaSelectValueContent u={u} />
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <EmpresaSearchInput u={u} />
            <div className="max-h-[300px] overflow-auto">
              <EmpresaOptionsContent u={u} />
            </div>
          </SelectContent>
        </Select>

        <EmpresaGroupModeButton u={u} />
      </div>
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input
        type="date"
        value={value}
        onChange={event => onChange(event.target.value || null)}
        className="h-9 w-[188px]"
      />
    </div>
  );
}

function HeaderFilters({ onAplicar, u }: { onAplicar: () => void; u: HeaderModel }) {
  return (
    <div className="flex items-end gap-3">
      <EmpresaField u={u} />
      <DateField
        label="Data Inicial"
        value={u.filtros.dataInicio || ''}
        onChange={u.setDataInicio}
      />
      <DateField label="Data Final" value={u.filtros.dataFim || ''} onChange={u.setDataFim} />
      <Button
        onClick={onAplicar}
        size="sm"
        className="h-9 bg-accent px-4 text-accent-foreground hover:bg-accent/90"
      >
        Aplicar
      </Button>
    </div>
  );
}

export function ServicosNfseHeader({ onAplicar }: ServicosNfseHeaderProps) {
  const u = useServicosNfseHeader();

  return (
    <header className="sticky top-0 z-50 border-b border-[#d9d9d9] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between gap-6 px-10 py-4">
        <HeaderBrand />
        <HeaderFilters onAplicar={onAplicar} u={u} />
      </div>
    </header>
  );
}
